import { useState, useCallback } from "react";
import {
	ConnFeatureFilter,
	PopularityFilters,
	FeatureClauses,
	FilterTypes,
	SortOption,
} from "../types/CDIFilter";
import {
	INITIAL_CONNECTOR_FILTERS,
	INITIAL_FEATURE_FILTERS,
	INITIAL_POPULARITY_FILTERS,
} from "../utils/filterConfig";
import {
	setLastDesignItemConnectorFilter,
	setLastDesignItemFeatureFilter,
	setLastDesignItemPopularityFilter,
	setLastDesignItemSortPreference,
} from "../services/lastDesignItemSearchFilter";

interface UseFiltersOptions {
	excludeFilters?: string[];
	onFilterChange?: () => void;
}

export const useFilters = (options: UseFiltersOptions = {}) => {
	const { excludeFilters = [], onFilterChange } = options;
	
	const [connectorFilters, setConnectorFilters] = useState<ConnFeatureFilter[]>(
		INITIAL_CONNECTOR_FILTERS,
	);
	const [featureFilters, setFeatureFilters] = useState<ConnFeatureFilter[]>(
		INITIAL_FEATURE_FILTERS,
	);
	const [popularityFilters, setPopularityFiltersState] = useState<PopularityFilters>(
		INITIAL_POPULARITY_FILTERS,
	);
	const [designCodeFilter, setDesignCodeFilter] = useState<string>("all");
	const [sortBy, setSortBy] = useState<SortOption>("relevancy");
	
	
	const setPopularityFilters = useCallback((filters: PopularityFilters) => {
		const filtered = { ...filters };
		
		if (excludeFilters.includes("MyBookmarks")) {
			filtered.MyBookmarks = FeatureClauses.None;
		}
		if (excludeFilters.includes("OthersBookmarks")) {
			filtered.OthersBookmarks = FeatureClauses.None;
		}
		
		setPopularityFiltersState(filtered);
	}, [excludeFilters]);
	
	const handleApplyFilters = useCallback(
		(filter: ConnFeatureFilter, filterType: FilterTypes) => {
			if (filterType === "connector") {
				const updated = connectorFilters.map((f) =>
					f.feature === filter.feature ? filter : f,
				);
				setConnectorFilters(updated);
				setLastDesignItemConnectorFilter({ connectorFilter: updated });
			} else if (filterType === "feature") {
				const updated = featureFilters.map((f) =>
					f.feature === filter.feature ? filter : f,
				);
				setFeatureFilters(updated);
				setLastDesignItemFeatureFilter({ featureFilter: updated });
			}
			
			onFilterChange?.();
		},
		[connectorFilters, featureFilters, onFilterChange],
	);
	
	const handleApplyPopularityFilter = useCallback(
		(filterKey: keyof PopularityFilters, clause: FeatureClauses) => {
			if (excludeFilters.includes(filterKey)) {
				return;
			}
			
			const updated = {
				...popularityFilters,
				[filterKey]: clause,
			};
			setPopularityFiltersState(updated);
			setLastDesignItemPopularityFilter({ popularityFilter: updated });
			onFilterChange?.();
		},
		[popularityFilters, excludeFilters, onFilterChange],
	);
	
	const handleApplySortPreference = useCallback((newSortBy: SortOption) => {
		if (newSortBy !== sortBy) {
			setSortBy(newSortBy);
			setLastDesignItemSortPreference(newSortBy);
			onFilterChange?.();
		}
	}, [onFilterChange, sortBy]);
	
	const handleSetOnlyFilter = useCallback(
		(label: string, filterType: FilterTypes) => {
			if (filterType === "connector") {
				const updated = connectorFilters.map((f) => ({
					...f,
					clause:
						f.feature === label ? FeatureClauses.Must : FeatureClauses.MustNot,
				}));
				setConnectorFilters(updated);
				setLastDesignItemConnectorFilter({ connectorFilter: updated });
			} else if (filterType === "feature") {
				const updated = featureFilters.map((f) => ({
					...f,
					clause:
						f.feature === label ? FeatureClauses.Must : FeatureClauses.MustNot,
				}));
				setFeatureFilters(updated);
				setLastDesignItemFeatureFilter({ featureFilter: updated });
			} else if (filterType === "popularity") {
				const filterKey = label as keyof PopularityFilters;
				
				if (excludeFilters.includes(filterKey)) {
					return;
				}
				
				const updated: PopularityFilters = {
					MyBookmarks:
						filterKey === "MyBookmarks"
							? FeatureClauses.Must
							: FeatureClauses.MustNot,
					OthersBookmarks:
						filterKey === "OthersBookmarks"
							? FeatureClauses.Must
							: FeatureClauses.MustNot,
				};
				setPopularityFiltersState(updated);
				setLastDesignItemPopularityFilter({ popularityFilter: updated });
			}
			
			onFilterChange?.();
		},
		[connectorFilters, featureFilters, excludeFilters, onFilterChange],
	);
	
	const clearAllFilters = useCallback(() => {
		setConnectorFilters(INITIAL_CONNECTOR_FILTERS);
		setFeatureFilters(INITIAL_FEATURE_FILTERS);
		setPopularityFiltersState(INITIAL_POPULARITY_FILTERS);
		setDesignCodeFilter("all");
		
		setLastDesignItemConnectorFilter({
			connectorFilter: INITIAL_CONNECTOR_FILTERS,
		});
		setLastDesignItemFeatureFilter({ featureFilter: INITIAL_FEATURE_FILTERS });
		setLastDesignItemPopularityFilter({
			popularityFilter: INITIAL_POPULARITY_FILTERS,
		});
		
		onFilterChange?.();
	}, [onFilterChange]);
	
	return {
		connectorFilters,
		featureFilters,
		popularityFilters,
		designCodeFilter,
		sortBy,
		setConnectorFilters,
		setFeatureFilters,
		setPopularityFilters,
		setDesignCodeFilter,
		setSortBy,
		handleApplyFilters,
		handleApplyPopularityFilter,
		handleApplySortPreference,
		handleSetOnlyFilter,
		clearAllFilters,
	};
};